// SHADOW RANKING™ — Full leaderboard page (additive).
// Route: /shadow/ranking
// Public read-only (no auth needed). Renders:
//   1. Header + animated stats bar
//   2. "Last month's champion" card (if any)
//   3. Ranked rows with thematic emojis by position
//   4. "How it works" collapsible explainer
//   5. Disclaimer footer
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Crown, Zap, Target, Flame, Gem, TrendingUp, Waves, Rocket,
  ArrowLeft, ChevronDown, ChevronUp, Trophy, ShoppingBag,
  Sparkles, Hourglass, Loader2,
} from "lucide-react";
import { API, safeJson } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { getShadowI18n } from "../i18n/shadow";
import { PremiosMensuales } from "../components/shadow/PremiosMensuales";

// Thematic emoji + Lucide icon + label per rank tier (label key in ui).
function rankTier(rank, ui) {
  if (rank === 1)   return { emoji: "👑", Icon: Crown,      label: ui.ranking_tier_KING,           color: "#fbbf24" };
  if (rank === 2)   return { emoji: "🥷", Icon: Sparkles,   label: ui.ranking_tier_SHADOW_MASTER,  color: "#c8d3e8" };
  if (rank === 3)   return { emoji: "⚡", Icon: Zap,        label: ui.ranking_tier_LIGHTNING,      color: "#facc15" };
  if (rank === 4)   return { emoji: "🎯", Icon: Target,     label: ui.ranking_tier_PRECISION,      color: "#fb923c" };
  if (rank === 5)   return { emoji: "🔥", Icon: Flame,      label: ui.ranking_tier_FIRE,           color: "#f97316" };
  if (rank <= 10)   return { emoji: "💎", Icon: Gem,        label: ui.ranking_tier_DIAMOND,        color: "#00d4ff" };
  if (rank <= 20)   return { emoji: "📈", Icon: TrendingUp, label: ui.ranking_tier_STRUCTURE,      color: "#22d3ee" };
  if (rank <= 30)   return { emoji: "🌊", Icon: Waves,      label: ui.ranking_tier_LIQUIDITY,      color: "#6e7fa5" };
  return                  { emoji: "🚀", Icon: Rocket,     label: ui.ranking_tier_RISING,         color: "#a78bfa" };
}

function accuracyColor(pct) {
  if (pct >= 81) return "#00e676";
  if (pct >= 61) return "#00d4ff";
  if (pct >= 41) return "#fbbf24";
  return "#ff3355";
}

const avatarColor = (id = "") => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffffff;
  const palette = ["#f97316", "#fbbf24", "#22d3ee", "#a855f7", "#10b981", "#ef4444", "#3b82f6", "#ec4899"];
  return palette[Math.abs(h) % palette.length];
};

const MONTH_LABELS_ES = {
  "01": "ENERO", "02": "FEBRERO", "03": "MARZO", "04": "ABRIL",
  "05": "MAYO", "06": "JUNIO", "07": "JULIO", "08": "AGOSTO",
  "09": "SEPTIEMBRE", "10": "OCTUBRE", "11": "NOVIEMBRE", "12": "DICIEMBRE",
};
const monthLabel = (ym, months) => {
  const map = months || MONTH_LABELS_ES;
  return ym ? `${map[ym.slice(5, 7)] || ym} ${ym.slice(0, 4)}` : "";
};

const ShadowRankingPage = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const ui = getShadowI18n(lang);
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  const [notifyState, setNotifyState] = useState("idle"); // idle | sending | done | error

  // Admin-only: trigger the monthly winners email batch.
  // Uses the previous month by default; the backend picks it if no `month` is sent.
  const handleNotifyWinners = async () => {
    if (!isAdmin) return;
    const lastMonth = data?.last_winner?.month;
    const confirmMsg = lastMonth
      ? `¿Enviar emails a los top 5 ganadores de ${lastMonth}?`
      : "¿Enviar emails a los top 5 ganadores del mes pasado?";
    if (!window.confirm(confirmMsg)) return;
    setNotifyState("sending");
    try {
      const url = lastMonth
        ? `${API}/shadow/ranking/notify-winners?month=${encodeURIComponent(lastMonth)}`
        : `${API}/shadow/ranking/notify-winners`;
      const res = await fetch(url, { method: "POST" });
      const json = await safeJson(res);
      if (!res.ok || !json?.ok) throw new Error(json?.detail || "request_failed");
      setNotifyState("done");
      window.alert(`✅ Emails programados para ${json.month}. Se envían en background.`);
    } catch (e) {
      setNotifyState("error");
      window.alert(`❌ Error: ${e.message}`);
    } finally {
      setTimeout(() => setNotifyState("idle"), 4000);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API}/ranking`);
        const json = await safeJson(res);
        if (!res.ok) throw new Error(json?.detail || ui.ranking_load_failed);
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = data?.stats || {};
  const rankings = data?.rankings || [];
  const last = data?.last_winner;

  return (
    <div className="min-h-screen bg-[#07080f] text-white">
      {/* Header */}
      <header className="border-b border-white/[0.06] bg-black/40 backdrop-blur-md">
        <div className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
          <Link
            to="/shadow"
            className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45 hover:text-white"
            data-testid="ranking-back-shadow"
          >
            <ArrowLeft className="h-3 w-3" />
            {ui.ranking_back_feed}
          </Link>
          <h1 className="mt-3 text-center text-[1.6rem] font-black tracking-tight sm:text-[2.2rem] lg:text-[2.8rem]">
            <span className="mr-2">👑</span>
            <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-amber-200 bg-clip-text text-transparent">
              SHADOW RANKING™
            </span>
            <span className="ml-2">👑</span>
          </h1>
          <p className="mt-1.5 text-center text-xs text-white/55 sm:text-sm">
            {ui.ranking_subtitle}
          </p>
          {isAdmin && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={handleNotifyWinners}
                disabled={notifyState === "sending"}
                data-testid="ranking-admin-notify-winners"
                className="inline-flex items-center gap-2 rounded-md border border-amber-400/35 bg-amber-400/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300 transition-colors hover:border-amber-400/60 hover:bg-amber-400/15 disabled:cursor-wait disabled:opacity-50"
              >
                {notifyState === "sending"
                  ? "⏳ ENVIANDO…"
                  : notifyState === "done"
                    ? "✅ EMAILS PROGRAMADOS"
                    : "📧 NOTIFICAR GANADORES"}
              </button>
            </div>
          )}
          <div className="mt-3 flex justify-center">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-cyan-200"
              data-testid="ranking-live-badge"
            >
              <span className="relative inline-flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400/80" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-400" />
              </span>
              <Sparkles className="h-2.5 w-2.5" /> {ui.ranking_updated_today}
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:py-8">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-white/50">
            <Loader2 className="h-4 w-4 animate-spin" /> {ui.ranking_loading}
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-rose-500/40 bg-rose-500/[0.08] p-4 text-xs text-rose-200">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <PremiosMensuales lang={lang} />
            {/* Stats bar */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="ranking-stats-bar">
              <StatCell icon="📊" value={stats.analyses_evaluated || 0} label={ui.ranking_stat_analyses} color="#00d4ff" />
              <StatCell icon="👥" value={stats.active_traders || 0} label={ui.ranking_stat_traders} color="#22d3ee" />
              <StatCell icon="🎯" value={`${stats.avg_score || 0}/100`} label={ui.ranking_stat_score_avg} color="#fbbf24" />
              <StatCell icon="⏰" value={stats.days_left || 0} label={ui.ranking_stat_days_left} color="#fb923c" />
            </div>

            {/* Last month winner */}
            {last && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mt-6 overflow-hidden rounded-xl border border-amber-400/40 bg-gradient-to-br from-amber-500/[0.08] via-orange-500/[0.04] to-zinc-950 p-5 sm:p-6"
                data-testid="ranking-last-winner"
              >
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-amber-300">
                  👑 {ui.ranking_winner_of} {monthLabel(last.month, ui.ranking_months)} 👑
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-black text-black shadow-[0_0_20px_rgba(255,180,0,0.45)]"
                    style={{ background: "#fbbf24" }}
                    aria-hidden
                  >
                    {(last.user?.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-lg font-black text-amber-100">{last.user?.name}</span>
                      {last.user?.is_pro && (
                        <span className="rounded-full border border-amber-400/40 bg-amber-400/[0.08] px-2 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-amber-200">
                          PRO
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/[0.08] px-2 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-amber-200">
                        <Trophy className="h-2.5 w-2.5" /> {ui.ranking_winner_badge}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-white/55">
                      {ui.ranking_winner_score} <span className="font-bold text-amber-200">{Math.round(last.score)}/100</span> ·
                      {" "}{ui.ranking_winner_accuracy} <span className="font-bold text-amber-200">{last.accuracy_percent}%</span> ·
                      {" "}{ui.ranking_winner_analyses} <span className="font-bold text-amber-200">{last.evaluated_posts}</span>
                    </p>
                  </div>
                </div>
                <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-amber-200/80">
                  <ShoppingBag className="h-3 w-3" />
                  {ui.ranking_winner_prize}
                </p>
              </motion.div>
            )}

            {/* Ranking table */}
            <div className="mt-6 space-y-1.5">
              {rankings.length === 0 ? (
                <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-8 text-center text-xs text-white/40" data-testid="ranking-empty">
                  {ui.ranking_empty}
                </div>
              ) : (
                rankings.map((row, idx) => <RankingRow key={row.user.id || idx} row={row} idx={idx} navigate={navigate} ui={ui} />)
              )}
            </div>

            {/* How it works (collapsible) */}
            <div className="mt-8 overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]">
              <button
                onClick={() => setHowOpen((v) => !v)}
                className="flex w-full items-center justify-between px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.22em] text-white/60 transition-colors hover:text-white"
                data-testid="ranking-how-toggle"
              >
                <span>{ui.ranking_how_toggle}</span>
                {howOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {howOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border-t border-white/[0.06] px-5 py-4 text-xs leading-relaxed text-white/60"
                >
                  <p className="mb-3">
                    {ui.ranking_how_intro_pre} <span className="font-bold text-white/85">{ui.ranking_how_intro_strong}</span>{" "}
                    {ui.ranking_how_intro_post}
                  </p>
                  <ul className="space-y-1.5">
                    <li>👑 <span className="font-bold text-white/85">{ui.ranking_how_b1_strong}</span> {ui.ranking_how_b1_rest}</li>
                    <li>🎯 <span className="font-bold text-white/85">{ui.ranking_how_b2_strong}</span> {ui.ranking_how_b2_rest}</li>
                    <li>⏰ <span className="font-bold text-white/85">{ui.ranking_how_b3_strong}</span>{ui.ranking_how_b3_rest}</li>
                    <li>❤️ <span className="font-bold text-white/85">{ui.ranking_how_b4_strong}</span> {ui.ranking_how_b4_rest}</li>
                  </ul>
                  <p className="mt-3 text-[11px] text-white/50">
                    {ui.ranking_how_footer_pre} <span className="font-bold text-white/75">{ui.ranking_how_footer_min}</span>{" "}
                    {ui.ranking_how_footer_mid} <span className="font-bold text-amber-200">{ui.ranking_how_footer_prize}</span>
                  </p>
                </motion.div>
              )}
            </div>

            <p className="mt-6 text-center text-[10px] leading-relaxed text-white/30">
              {ui.ranking_disclaimer}
            </p>
          </>
        )}
      </main>
    </div>
  );
};

const StatCell = ({ icon, value, label, color }) => (
  <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-3 text-center sm:px-4 sm:py-4">
    <div className="text-base sm:text-lg">{icon}</div>
    <div className="mt-1 font-mono text-lg font-black sm:text-2xl" style={{ color }}>{value}</div>
    <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-white/45 sm:text-[10px]">{label}</div>
  </div>
);

const RankingRow = ({ row, idx, navigate, ui }) => {
  const tier = useMemo(() => rankTier(row.rank, ui), [row.rank, ui]);
  const acc = row.accuracy_percent || 0;
  const accColor = accuracyColor(acc);
  const [barWidth, setBarWidth] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setBarWidth(acc), 300 + idx * 50);
    return () => clearTimeout(t);
  }, [acc, idx]);

  const goToUserFeed = () => navigate(`/shadow?author=${encodeURIComponent(row.user.id || "")}`);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: idx * 0.05 }}
      className="group rounded-xl border border-white/[0.07] bg-white/[0.015] px-4 py-3 transition-colors hover:border-cyan-400/30 sm:px-5 sm:py-3.5"
      data-testid={`ranking-row-${row.rank}`}
    >
      <div className="flex items-center gap-3">
        {/* Position emoji + #N */}
        <div className="flex flex-col items-center" style={{ minWidth: 44 }}>
          <span className="text-xl leading-none sm:text-2xl">{tier.emoji}</span>
          <span className="mt-1 font-mono text-[9px] font-bold tracking-widest" style={{ color: tier.color }}>
            #{row.rank}
          </span>
        </div>

        {/* Avatar */}
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-black text-[#07080f]"
          style={{ background: row.is_winner ? "#fbbf24" : avatarColor(row.user.id || row.user.name) }}
          aria-hidden
        >
          {(row.user.name || "?").charAt(0).toUpperCase()}
        </div>

        {/* Main info */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-bold text-white">{row.user.name}</span>
            {row.user.is_admin && (
              <span className="rounded-full border border-cyan-400/40 bg-cyan-400/[0.08] px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-cyan-200">
                {ui.ranking_admin}
              </span>
            )}
            {row.user.is_pro && (
              <span className="rounded-full border border-amber-400/40 bg-amber-400/[0.08] px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-amber-200">
                PRO
              </span>
            )}
            {row.is_winner && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/50 bg-amber-400/[0.12] px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-amber-200">
                <Trophy className="h-2.5 w-2.5" /> {ui.ranking_winner_badge}
              </span>
            )}
          </div>
          <p className="mt-0.5 hidden font-mono text-[9px] uppercase tracking-[0.18em] sm:block" style={{ color: tier.color }}>
            {tier.label}
          </p>

          {/* Accuracy bar */}
          <div className="mt-2 flex items-center gap-2">
            <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/[0.06]">
              <div
                className="h-full rounded-full transition-[width] duration-1000 ease-out"
                style={{ width: `${barWidth}%`, background: accColor, transitionTimingFunction: "cubic-bezier(0.33,1,0.68,1)" }}
              />
            </div>
            <span className="font-mono text-[10px] font-bold" style={{ color: accColor }}>
              {acc}%
            </span>
          </div>
          <p className="mt-1.5 text-[10px] text-white/40">
            {row.total_posts} {ui.ranking_row_analyses} · {row.evaluated_posts} {ui.ranking_row_evaluated} · {row.correct_posts} {ui.ranking_row_correct}
          </p>
        </div>

        {/* Score + CTA */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="font-mono text-lg font-black text-cyan-200 sm:text-xl">{Math.round(row.total_score)}<span className="text-[10px] text-white/35">/100</span></div>
          <button
            onClick={goToUserFeed}
            className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.18em] text-white/35 transition-colors hover:text-cyan-200"
            data-testid={`ranking-row-cta-${row.rank}`}
          >
            {ui.ranking_view_analyses}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ShadowRankingPage;
